import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from './ui/badge';
import { Calendar, Star, Filter, X } from 'lucide-react';
import { Therapist, Specialty } from '../types/user';
import { AppointmentModal } from '../components/appointment-modal';
import { fetchTherapists } from '../api/therapist';
import { Alert, AlertDescription } from '../components/ui/alert';

const specialties: Specialty[] = [
  { id: '1', name: 'Développement Personnel', color: 'bg-blue-100 text-blue-800' },
  { id: '2', name: 'Vie Professionnelle', color: 'bg-purple-100 text-purple-800' },
  { id: '3', name: 'Relations', color: 'bg-pink-100 text-pink-800' },
  { id: '4', name: 'Stress', color: 'bg-yellow-100 text-yellow-800' },
  { id: '5', name: 'Confiance en Soi', color: 'bg-green-100 text-green-800' },
  { id: '6', name: 'Bien-être', color: 'bg-teal-100 text-teal-800' },
  { id: '7', name: 'Équilibre Vie Pro/Perso', color: 'bg-indigo-100 text-indigo-800' },
  { id: '8', name: 'Motivation', color: 'bg-orange-100 text-orange-800' }
];

const TherapistCard = ({ therapist }: { therapist: Therapist }) => {
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  
  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6 transition-all duration-300 hover:shadow-lg">
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
      <div className="w-full md:w-[150px] aspect-square md:aspect-auto overflow-hidden rounded-lg">
        <img
          src={therapist.imageUrl}
          alt={therapist.name}
          className="w-full h-full object-cover object-top"
        />
      </div>
        <div className="flex-1">
          <div className="flex flex-col md:flex-row justify-between items-start gap-2">
            <div>
              <h3 className="text-xl font-semibold mb-1">{therapist.name}</h3>
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <Star className="w-4 h-4 fill-yellow-400 stroke-yellow-400" />
                <span>{therapist.rating}</span>
                <span className="mx-2">•</span>
                <span>{therapist.yearsExperience} ans d'expérience</span>
              </div>
            </div>
            <div className="text-sm text-gray-600 w-full md:w-auto">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Disponible le {new Date(therapist.nextAvailable).toLocaleDateString('fr-FR')}</span>
              </div>
            </div>
          </div>

          <p className="text-gray-600 mb-4">{therapist.bio}</p>

          <div className="flex flex-wrap gap-2 mb-4">
            {therapist.specialties.map((specialty) => (
              <Badge
                key={specialty.id}
                className={`${specialty.color} border-none font-normal`}
              >
                {specialty.name}
              </Badge>
            ))}
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="text-sm text-gray-600">
              Langues: {therapist.languages.join(', ')}
            </div>
            <Button 
              className="w-full md:w-auto text-white"
              style={{backgroundColor: '#00c4cc'}}
              onClick={() => setShowAppointmentModal(true)}
            >
              Prendre rendez-vous
            </Button>
          </div>
        </div>

        <AppointmentModal 
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        therapist={therapist}
      />
      </div>
    </div>
  );
};


const TherapistList = () => {
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [displayedTherapists, setDisplayedTherapists] = useState<Therapist[]>([]);
  const [allTherapists, setAllTherapists] = useState<Therapist[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTherapists = async () => {
      try {
        setIsLoading(true);
        const therapists = await fetchTherapists();
        setAllTherapists(therapists);
        setDisplayedTherapists(therapists);
      } catch (err) {
        setError('Une erreur est survenue lors du chargement des professionnels.');
        console.error('Error fetching therapists:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTherapists();
  }, []);

  useEffect(() => {
    if (selectedSpecialties.length === 0) {
      setDisplayedTherapists(allTherapists);
      return;
    }

    const filtered = allTherapists.filter(therapist =>
      therapist.specialties.some(specialty =>
        selectedSpecialties.includes(specialty.id)
      )
    );
    setDisplayedTherapists(filtered);
  }, [selectedSpecialties, allTherapists]);

  const toggleSpecialty = (specialtyId: string) => {
    setSelectedSpecialties(prev =>
      prev.includes(specialtyId)
        ? prev.filter(id => id !== specialtyId)
        : [...prev, specialtyId]
    );
  };

  const clearFilters = () => {
    setSelectedSpecialties([]);
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00c4cc]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto py-8 px-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Nos Professionnels</h1>
        <div className="w-full md:w-auto">
          <Button
            variant="outline"
            className="w-full md:w-auto mb-2 md:mb-0"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres {selectedSpecialties.length > 0 && `(${selectedSpecialties.length})`}
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-semibold">Spécialités</h2>
            {selectedSpecialties.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4 mr-1" />
                Effacer les filtres
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {specialties.map((specialty) => (
              <Badge
                key={specialty.id}
                className={`cursor-pointer transition-all duration-200 ${
                  selectedSpecialties.includes(specialty.id)
                    ? specialty.color
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } border-none font-normal`}
                onClick={() => toggleSpecialty(specialty.id)}
              >
                {specialty.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {displayedTherapists.length === 0 ? (
        <div className="text-center py-8 text-gray-600">
          Aucun professionnel ne correspond à vos critères.
          <br />
          <Button 
            variant="link" 
            onClick={clearFilters}
            className="text-[#00c4cc]"
          >
            Effacer les filtres
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {displayedTherapists.map((therapist) => (
            <TherapistCard key={therapist.id} therapist={therapist} />
          ))}
        </div>
      )}
    </div>
  );
};

export default TherapistList;